/**
 * Sections that arrive as you scroll to them.
 *
 * **The class is added by script, never by the stylesheet.** Hiding an element
 * in CSS and revealing it in JavaScript means a bundle that fails to load
 * leaves a blank page — and the home page is the one page a visitor sees
 * before deciding whether to trust any of this. Everything is visible by
 * default; the animation is what gets added, not the content.
 *
 * `IntersectionObserver` rather than a scroll listener: the browser does the
 * work off the main thread, and a scroll handler firing sixty times a second
 * to compute bounding boxes is the sort of thing that makes a page feel heavy
 * while trying to make it feel lively.
 */

/** Reveals once and stops watching: a section does not un-arrive. */
export function initReveal(root: ParentNode = document): void {
  const targets = root.querySelectorAll<HTMLElement>('[data-reveal]');
  if (targets.length === 0) return;

  /*
   * Somebody who asked their system for less motion has asked for this too.
   * Marking them visible immediately, without arming the observer, is both
   * the accessible answer and the cheap one.
   */
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (still || typeof IntersectionObserver === 'undefined') {
    for (const target of targets) target.classList.add('is-visible');
    return;
  }

  // Only now does the stylesheet start hiding anything.
  document.documentElement.classList.add('js-reveal');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    // A little before the edge, so the movement finishes as the section
    // settles rather than starting once it is already being read.
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
  );

  for (const target of targets) observer.observe(target);
}
